import type { OverflowMenuItems } from '../../types';
import type { ReactNode } from 'react';
import type { AssignmentStatusNames } from '~/types';
import { Body, Card, Icon, IconButton, Row, Col, Button, Span } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { ASSIGNMENT_STATUS_NAMES, AssignmentStatusEnum } from '~/constants';
import { FooterWrapper, FullHeightRow } from '../../styles';
import { CardOverflowMenu } from '../OverflowCardMenu';

const { t } = I18n;

const STATUSES_TO_DISPLAY = {
  [AssignmentStatusEnum.OVERDUE]: ASSIGNMENT_STATUS_NAMES.overdue,
  [AssignmentStatusEnum.NEEDS_GRADING]: ASSIGNMENT_STATUS_NAMES.needs_grading,
  [AssignmentStatusEnum.FAILED]: ASSIGNMENT_STATUS_NAMES.failed,
};

interface CardFooterProps {
  assignmentStatus?: AssignmentStatusNames;
  inProgressMessage?: string;
  startContentMessage?: string;
  onStart?: () => void;
  onPreview?: () => void;
  overflowMenuItems?: OverflowMenuItems;
  hideContentCTA?: boolean;
  actions?: ReactNode;
}

const PREVIEW = t('js.learner.libraries.preview');
const COMPLETED = t('common.assignment_status.completed');

export const CardFooter = ({
  assignmentStatus,
  inProgressMessage,
  startContentMessage,
  onStart,
  onPreview,
  overflowMenuItems = {},
  hideContentCTA = false,
  actions,
}: CardFooterProps) => {
  const theme = useTheme();

  const showAssignmentStatus = Object.keys(STATUSES_TO_DISPLAY).includes(assignmentStatus as AssignmentStatusEnum);
  const showStartContentCTA = !assignmentStatus || assignmentStatus === AssignmentStatusEnum.NOT_STARTED;
  const showProgress = assignmentStatus === AssignmentStatusEnum.IN_PROGRESS;
  const showCompleted = assignmentStatus === AssignmentStatusEnum.COMPLETED;
  const showOverflowMenu = Boolean(Object.keys(overflowMenuItems || {}).length);

  return (
    <Card.Footer size={0}>
      <FooterWrapper>
        <FullHeightRow align="baseline" justify={hideContentCTA ? 'flex-end' : 'space-between'}>
          {!hideContentCTA && (
            <Col>
              {showAssignmentStatus && (
                <Body color={theme.colors.on[600]} level="md" unselectable="on">
                  {STATUSES_TO_DISPLAY[assignmentStatus]}
                </Body>
              )}
              {showStartContentCTA && onStart && (
                <Row justify="space-between">
                  <Col xs="auto">
                    <Span color={theme.colors.on[600]}>
                      <Button
                        onPress={onStart}
                        color="secondary"
                        variant="text-inline"
                        data-testid="guide-card-button-start"
                      >
                        {startContentMessage}
                      </Button>
                    </Span>
                  </Col>
                </Row>
              )}
              {showProgress && (
                <Body color={theme.colors.on[600]} level="md" unselectable="on">
                  {inProgressMessage}
                </Body>
              )}
              {showCompleted && (
                <Body color={theme.colors.on[600]} level="md" unselectable="on">
                  <Icon name="check-circle:regular" size="md" /> {COMPLETED}
                </Body>
              )}
            </Col>
          )}

          {(showOverflowMenu || actions || showStartContentCTA) && (
            <Col xs="auto">
              {showStartContentCTA && onPreview && (
                <IconButton
                  onPress={onPreview}
                  color="secondary"
                  variant="text"
                  size="md"
                  label={PREVIEW}
                  iconName="eye-open:regular"
                  data-testid="card-button-preview"
                />
              )}
              {actions}
              {showOverflowMenu && <CardOverflowMenu overflowMenuItems={overflowMenuItems} />}
            </Col>
          )}
        </FullHeightRow>
      </FooterWrapper>
    </Card.Footer>
  );
};
